// 1.2.16: 性能优化 - 使用 memo 和 useMemo 减少重复渲染
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Star, DollarSign, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';
import type { Restaurant } from '../../features/search/types/restaurant';
import { RestaurantImage } from './RestaurantImage';

interface RestaurantCardProps {
  restaurant: Restaurant;
  index: number;
  variant?: 'search' | 'chat';
  isFavorite?: boolean;
  onFavoriteClick?: (restaurant: Restaurant) => void;
}

export const RestaurantCard = memo(function RestaurantCard({ 
  restaurant, 
  index, 
  variant = 'search',
  isFavorite = false,
  onFavoriteClick
}: RestaurantCardProps) {
  const { t, i18n } = useTranslation();
  
  // 1.2.16: 使用 useMemo 优化计算，避免每次渲染都重新计算
  const displayName = useMemo(() => {
    const language = i18n.language;
    if (language === 'zh') {
      return restaurant.nameZh || restaurant.name;
    } else if (language === 'ja') {
      return restaurant.nameJa || restaurant.name;
    } else {
      return restaurant.name;
    }
  }, [i18n.language, restaurant.name, restaurant.nameZh, restaurant.nameJa]);
  
  const displayDescription = useMemo(() => {
    return i18n.language === 'ja' && restaurant.descriptionJa 
      ? restaurant.descriptionJa 
      : restaurant.description;
  }, [i18n.language, restaurant.description, restaurant.descriptionJa]);
  
  // Debug: 检查 url 字段（现在应该总是有 URL，因为会手动构建）
  if (restaurant.address && !restaurant.url) {
    if (import.meta.env.DEV) {
      console.error('❌ 餐厅地址存在但缺少 URL（这不应该发生）:', {
        name: restaurant.name,
        address: restaurant.address,
        url: restaurant.url,
        id: restaurant.id
      });
    }
  }
  
  // 1.2.16: 使用 useMemo 优化预算标签计算
  const budgetLabel = useMemo(() => {
    switch (restaurant.budget) {
      case 'low': return t('budgetLow');
      case 'mid': return t('budgetMid');
      case 'high': return t('budgetHigh');
      default: return restaurant.budget;
    }
  }, [restaurant.budget, t]);

  // 2.0.0: 容器样式 — 亮/暗双主题均使用橘色描边
  const containerClassName = useMemo(() => {
    return variant === 'chat' 
      ? "bg-white dark:bg-gray-900 rounded-2xl shadow-lg dark:shadow-gray-900/50 overflow-hidden hover:shadow-xl dark:hover:shadow-gray-800/50 transition-shadow duration-300 max-w-sm w-full border border-orange-300 dark:border-orange-500"
      : "bg-white dark:bg-gray-900 rounded-2xl shadow-lg dark:shadow-gray-900/50 overflow-hidden hover:shadow-xl dark:hover:shadow-gray-800/50 transition-shadow duration-300 border border-orange-300 dark:border-orange-500";
  }, [variant]);

  // 1.2.16: 使用 useMemo 优化 Google Maps URL 构建
  const googleMapsUrl = useMemo(() => {
    return restaurant.url || 
      (restaurant.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(restaurant.address)}` : '#');
  }, [restaurant.url, restaurant.address]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className={containerClassName}
    >
      {/* 图片区域 - 可点击跳转到 Google Maps */}
      <a
        href={googleMapsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="block relative h-40 xs:h-44 sm:h-48 overflow-hidden cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <RestaurantImage
          imageUrl={restaurant.image}
          alt={displayName}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          fallbackSrc="https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=800&auto=format&fit=crop"
        />
        {/* 1.2.15: 收藏按钮 - 位于图片右上角 */}
        {onFavoriteClick && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onFavoriteClick(restaurant);
            }}
            className="absolute top-2 xs:top-2.5 sm:top-3 right-2 xs:right-2.5 sm:right-3 p-1.5 xs:p-2 rounded-full bg-white dark:bg-gray-800 bg-opacity-90 dark:bg-opacity-90 backdrop-blur-sm hover:bg-opacity-100 transition-all duration-200 z-20"
            aria-label={isFavorite ? t('removeFromFavorites') : t('addToFavorites')}
            title={isFavorite ? t('removeFromFavorites') : t('addToFavorites')}
          >
            <Star 
              className={`w-4 h-4 xs:w-5 xs:h-5 transition-colors duration-200 ${
                isFavorite 
                  ? 'text-yellow-500 fill-yellow-500' 
                  : 'text-gray-400 hover:text-yellow-400'
              }`}
            />
          </button>
        )}
      </a>

      <div className="p-3 xs:p-4 sm:p-5">
        {/* 餐厅名称 - 可点击跳转到 Google Maps */}
        <a
          href={googleMapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block text-lg xs:text-xl font-bold text-gray-900 dark:text-white mb-1 hover:text-blue-600 dark:hover:text-blue-400 hover:underline transition-colors duration-200 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {displayName}
        </a>
        {/* 1.2.7 评分移动到餐厅类型右侧 */}
        <div className="flex items-center gap-2 mb-2 xs:mb-3">
          <p className="text-xs xs:text-sm text-orange-600 font-medium">
            {restaurant.cuisine}
          </p>
          {restaurant.rating > 0 && (
            <div className="flex items-center gap-0.5 xs:gap-1">
              <Star className="w-3.5 h-3.5 xs:w-4 xs:h-4 text-yellow-500 fill-yellow-500" />
              <span className="text-xs xs:text-sm font-semibold text-gray-700 dark:text-gray-300">{restaurant.rating.toFixed(1)}</span>
            </div>
          )}
        </div>

        {/* 只有当 description 和 address 不同时才显示 description */}
        {displayDescription && displayDescription !== restaurant.address && (
          <p className="text-gray-600 dark:text-gray-400 text-xs xs:text-sm mb-3 xs:mb-4 line-clamp-2">
            {displayDescription}
          </p>
        )}

        {/* 消费等级 */}
        <div className="flex items-center gap-1 mb-1.5 xs:mb-2 text-xs xs:text-sm text-gray-500 dark:text-gray-400">
          <DollarSign className="w-3.5 h-3.5 xs:w-4 xs:h-4" />
          <span>{budgetLabel}</span>
        </div>

        {/* 地址单独一行 */}
        {restaurant.address && (
          <div className="flex items-start gap-1 mb-3 xs:mb-4 text-xs xs:text-sm">
            <MapPin className="w-3.5 h-3.5 xs:w-4 xs:h-4 mt-0.5 flex-shrink-0" />
            {/* 地址链接 - 跳转到 Google Maps */}
            <a
              href={googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline break-words cursor-pointer relative z-10"
              onClick={(e) => {
                e.stopPropagation();
              }}
              style={{ pointerEvents: 'auto' }}
            >
              {restaurant.address}
            </a>
          </div>
        )}

        <div className="flex flex-wrap gap-1.5 xs:gap-2">
          {restaurant.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="px-2 xs:px-2.5 sm:px-3 py-0.5 xs:py-1 text-xs font-medium rounded-full bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </motion.div>
  );
}, (prevProps, nextProps) => {
  // 1.2.16: 自定义比较函数，只有当关键 props 变化时才重新渲染
  return (
    prevProps.restaurant.id === nextProps.restaurant.id &&
    prevProps.isFavorite === nextProps.isFavorite &&
    prevProps.variant === nextProps.variant &&
    prevProps.index === nextProps.index &&
    prevProps.onFavoriteClick === nextProps.onFavoriteClick
  );
});
